import { useState } from 'react'

import styled from '@emotion/styled'
import { useForm } from 'react-hook-form'

import { breakpoints, Flex, ProgressCircular, spacing, Text } from '@cash-design-system/react'
import { AuthorizeDigitalWalletPaymentBlocker } from '@cash-web/protos/squareup/franklin/blockers/authorize_digital_wallet_blocker.pb'
import { useTextTokenizer } from '@cash-web/shared-feature-use-text-tokenizer'
import useMediaQuery from '@cash-web/shared-ui-use-media-query'

import { BaseBlocker } from '../BaseBlocker/BaseBlocker'
import { ApplePayButton } from './ApplePayButton/ApplePayButton'
import { GooglePayButton } from './GooglePayButton/GooglePayButton'
import { isValidMoney } from './validity'

const ProgressContainer = styled(Flex)`
  padding: ${spacing.xl};
  align-items: center;
  justify-content: center;
`

export const BlockerAuthorizeDigitalWalletPayment = ({
  blocker,
}: {
  blocker: AuthorizeDigitalWalletPaymentBlocker
}) => {
  const [submitting, setSubmitting] = useState(false)

  const textTokenizer = useTextTokenizer()
  const isMobile = !useMediaQuery(breakpoints.tablet)
  const form = useForm()

  if (!isValidMoney(blocker.authorization_amount)) {
    throw Error(
      `BlockerAuthorizeDigitalWalletPayment: authorization_amount is invalid [amount=${blocker.authorization_amount?.amount}] [currency_code=${blocker.authorization_amount?.currency_code}]`
    )
  }

  if (!blocker.card_brand_types || blocker.card_brand_types.length === 0) {
    throw Error(
      `BlockerAuthorizeDigitalWalletPayment: card_brand_types is invalid [card_brand_types=${blocker.card_brand_types}]`
    )
  }

  if (!blocker.supported_cash_instrument_types || blocker.supported_cash_instrument_types.length === 0) {
    throw Error(
      `BlockerAuthorizeDigitalWalletPayment: supported_cash_instrument_types is invalid [supported_cash_instrument_types=${blocker.supported_cash_instrument_types}]`
    )
  }

  if (!blocker.allowable_issuer_countries) {
    throw Error(
      `BlockerAuthorizeDigitalWalletPayment: allowable_issuer_countries is invalid [allowable_issuer_countries=${blocker.allowable_issuer_countries}]`
    )
  }

  return submitting ? (
    <ProgressContainer>
      <ProgressCircular size="large" />
    </ProgressContainer>
  ) : (
    <BaseBlocker
      mainText={blocker.title}
      secondaryText={
        blocker.subtitle && (
          <Text typography="body" color="textSubtle">
            {textTokenizer(blocker.subtitle)}
          </Text>
        )
      }
      form={form}
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onSubmit={() => {}}
      buttons={[
        <GooglePayButton
          key="google-pay"
          amount={blocker.authorization_amount}
          cashInstrumentTypes={blocker.card_brand_types}
          billingAddressRequired={blocker.require_billing_address}
          phoneNumberRequired={blocker.require_payer_phone}
          emailRequired={blocker.require_payer_email}
          shippingAddressRequired={blocker.require_shipping_address}
          supportedInstrumentTypes={blocker.supported_cash_instrument_types}
          allowableIssuerCountries={[blocker.allowable_issuer_countries]}
          setSubmitting={setSubmitting}
        />,
        <ApplePayButton
          key="apple-pay"
          amount={blocker.authorization_amount}
          processingFee={blocker.processing_fee}
          cashInstrumentTypes={blocker.card_brand_types}
          requirePayerName={blocker.require_payer_name}
          requirePhoneNumber={blocker.require_payer_phone}
          requirePayerEmail={blocker.require_payer_email}
          requireBillingAddress={blocker.require_billing_address}
          requireShippingAddress={blocker.require_shipping_address}
          supportedInstrumentTypes={blocker.supported_cash_instrument_types}
          allowableIssuerCountries={[blocker.allowable_issuer_countries]}
          setSubmitting={setSubmitting}
        />,
      ]}
      shouldStackButtons={isMobile}
      disclaimerText={blocker.disclaimer_text}
    />
  )
}
